import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGroupSessions } from '@/hooks/useGroupSessions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Users, Plus, Edit, Trash2, Clock, UserCheck } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { GroupSession } from '@/types/groupSession';

const getGroupTypeIcon = (type: string) => {
  switch (type) {
    case 'family': return '👨‍👩‍👧‍👦';
    case 'yoga': return '🧘‍♀️';
    case 'organization': return '🏢';
    case 'team': return '👥';
    case 'friends': return '👫';
    default: return '📁';
  }
};

const getGroupTypeColor = (type: string) => {
  switch (type) {
    case 'family': return 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-300';
    case 'yoga': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
    case 'organization': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    case 'team': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    case 'friends': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
    default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
  }
};

export default function GroupsPage() {
  const navigate = useNavigate();
  const { groupSessions, loading, deleteGroupSession } = useGroupSessions();
  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null);

  const activeGroups = groupSessions.filter(group => group.is_active);
  const groupsByType = activeGroups.reduce((acc, group) => {
    const type = group.group_type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(group);
    return acc;
  }, {} as Record<string, GroupSession[]>);

  const handleDeleteGroup = async (groupId: string) => {
    try {
      setDeletingGroupId(groupId);
      await deleteGroupSession(groupId);
    } catch (error) {
      console.error('Failed to delete group:', error);
    } finally {
      setDeletingGroupId(null);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-muted rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">My Groups</h1>
          <p className="text-muted-foreground">
            Manage your group sessions and participants
          </p>
        </div>
        <Button onClick={() => navigate('/groups/new')} className="gap-2">
          <Plus className="h-4 w-4" />
          Create New Group
        </Button>
      </div>

      {activeGroups.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent className="space-y-4">
            <Users className="h-12 w-12 mx-auto text-muted-foreground" />
            <div>
              <h3 className="text-lg font-semibold">No groups yet</h3>
              <p className="text-muted-foreground">
                Create your first group to start sharing sessions with family, friends, or colleagues.
              </p>
            </div>
            <Button onClick={() => navigate('/groups/new')} className="gap-2">
              <Plus className="h-4 w-4" />
              Create Your First Group
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupsByType).map(([type, groups]) => (
            <div key={type} className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{getGroupTypeIcon(type)}</span>
                <h2 className="text-xl font-semibold capitalize">
                  {type === 'organization' ? 'Organization' : type} Groups
                </h2>
                <Badge variant="secondary">{groups.length}</Badge>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {groups.map((group) => (
                  <Card key={group.id} className="group hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <CardTitle className="text-lg">{group.group_name}</CardTitle>
                          <Badge className={getGroupTypeColor(group.group_type)} variant="secondary">
                            {group.group_type}
                          </Badge>
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/groups/${group.id}`)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" disabled={deletingGroupId === group.id}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Group</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{group.group_name}"? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteGroup(group.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {group.description && (
                        <p className="text-sm text-muted-foreground">{group.description}</p>
                      )}
                      
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <UserCheck className="h-4 w-4" />
                          <span>{group.participants?.length || 0} participants</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          <span>{formatDistanceToNow(new Date(group.created_at))} ago</span>
                        </div>
                      </div>

                      {group.participants && group.participants.length > 0 && (
                        <>
                          <Separator />
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                              Participants
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {group.participants.slice(0, 3).map((participant, index) => (
                                <Badge key={index} variant="outline" className="text-xs">
                                  {participant.firstName}
                                </Badge>
                              ))}
                              {group.participants.length > 3 && (
                                <Badge variant="outline" className="text-xs text-muted-foreground">
                                  +{group.participants.length - 3} more
                                </Badge>
                              )}
                            </div>
                          </div>
                        </>
                      )}

                      <Button
                        variant="outline"
                        className="w-full mt-4"
                        onClick={() => navigate(`/groups/${group.id}`)}
                      >
                        View Details
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}